/**
 * Convergence of Passions & Careers — diagram plus an equal-weight list.
 *
 * The list is not a fallback bolted on afterwards: on mobile, on a screen
 * reader, and on paper it *is* the artefact. Selecting a circle in either
 * view selects it in the other, by keyboard or by tap, and every circle
 * carries the questions that produced it so the whole thing can be audited.
 */
import { memo, useId, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  CONFIDENCE_LABEL, CONFIDENCE_SHORT, convergenceText,
  type Convergence, type ConvergenceCircle,
} from "../convergence";

const RING = [
  "hsl(174 100% 20%)", "hsl(239 97% 49%)", "hsl(20 100% 56%)",
  "hsl(226 87% 34%)", "hsl(174 60% 38%)", "hsl(20 80% 40%)",
  "hsl(239 60% 65%)", "hsl(0 0% 45%)",
];

/** Ring centres on a circle of radius 26 around the convergence point. */
function layout(n: number): { cx: number; cy: number; r: number }[] {
  const r = n <= 3 ? 30 : n <= 5 ? 26 : 23;
  const orbit = n === 1 ? 0 : n <= 3 ? 16 : n <= 5 ? 22 : 26;
  return Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    return { cx: 50 + Math.cos(a) * orbit, cy: 50 + Math.sin(a) * orbit, r };
  });
}

function CircleCard({
  circle, index, selected, onSelect, convergedIds,
}: {
  circle: ConvergenceCircle;
  index: number;
  selected: boolean;
  onSelect: () => void;
  convergedIds: Set<string>;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
          "w-full min-h-11 rounded-[24px] border p-4 text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          selected ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-muted/40",
        )}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block h-3 w-3 shrink-0 rounded-full"
            style={{ background: RING[index % RING.length] }}
          />
          <span className="font-semibold">{circle.label}</span>
          <span className="text-xs text-muted-foreground">
            {circle.kind === "observation"
              ? "noticed about you, approved by you"
              : circle.kind === "signal"
              ? "from how you answered"
              : "something you told us"}
          </span>
        </span>
        <span className="mt-1 block text-sm text-muted-foreground">{circle.rationale}</span>

        <span className="mt-3 block text-sm">
          <span className="font-medium">Roles inside this circle</span>
          <ul className="mt-1 space-y-1">
            {circle.roles.map((r) => (
              <li key={r.ciId} className="flex items-baseline justify-between gap-3">
                <Link
                  to={`/careers/${r.ciId}`}
                  className="underline decoration-dotted underline-offset-4 hover:decoration-solid"
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.title}
                </Link>
                <span className="shrink-0 text-xs text-muted-foreground">
                  fit {r.fit}
                  {convergedIds.has(r.ciId) && " · converges"}
                </span>
              </li>
            ))}
          </ul>
        </span>

        {selected && (
          <span className="mt-3 block rounded-2xl bg-muted/50 p-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">What put this circle here</span>
            {circle.families.length > 0 && (
              <span className="mt-1 block">
                Career families: {circle.families.join(", ")}
              </span>
            )}
            <ul className="mt-2 space-y-1">
              {circle.inputs.map((i, k) => (
                <li key={`${i.itemCode}-${k}`}>
                  <span className="font-mono">{i.itemCode}</span> · {i.question}
                  {" → "}
                  <span className="text-foreground">{i.answer}</span>
                </li>
              ))}
            </ul>
          </span>
        )}
      </button>
    </li>
  );
}

export const ConvergenceVenn = memo(function ConvergenceVenn({
  data, printable = false,
}: { data: Convergence; printable?: boolean }) {
  const [selected, setSelected] = useState<string | null>(null);
  const titleId = useId();
  const geo = layout(data.circles.length);
  const convergedIds = new Set(data.converged.map((r) => r.ciId));

  if (data.empty) {
    return (
      <section aria-labelledby={titleId} className="space-y-3">
        <h2 id={titleId} className="text-2xl font-semibold">Where your interests meet</h2>
        <p className="text-muted-foreground">
          You told us about one thing you're drawn to, so there's nothing to overlap yet. Add another interest
          and we'll show you where they cross.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby={titleId} className="space-y-5">
      <header className="space-y-2">
        <h2 id={titleId} className="text-2xl font-semibold">Where your interests meet</h2>
        <p className="text-muted-foreground">{data.method}</p>
        {data.purpose && <p className="font-medium">{data.purpose}</p>}
        {!printable && (
          <button
            type="button"
            onClick={() => {
              const blob = new Blob([convergenceText(data)], { type: "text/plain;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "wekit-career-clarity-360-convergence.txt";
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="inline-flex min-h-11 items-center rounded-full border border-border px-4 text-sm font-medium hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Download this as text
          </button>
        )}
      </header>

      {/* Diagram. Decorative: everything in it is stated in the list below. */}
      <div className={cn("mx-auto w-full max-w-md", printable ? "" : "hidden sm:block")}>
        <svg
          viewBox="0 0 100 100"
          role="img"
          aria-label={`Diagram of ${data.circles.length} overlapping interest circles: ${data.circles.map((c) => c.label).join(", ")}. The same information is listed below.`}
          className="h-auto w-full motion-safe:transition-opacity"
        >
          {data.circles.map((c, i) => {
            const g = geo[i];
            const on = selected === null || selected === c.id;
            return (
              <circle
                key={c.id}
                cx={g.cx} cy={g.cy} r={g.r}
                fill={RING[i % RING.length]}
                fillOpacity={on ? 0.18 : 0.05}
                stroke={RING[i % RING.length]}
                strokeOpacity={on ? 0.9 : 0.25}
                strokeWidth={0.6}
              />
            );
          })}
          {data.converged.length > 0 && (
            <>
              <circle cx="50" cy="50" r="6" fill="hsl(var(--background))" fillOpacity={0.85} />
              <text
                x="50" y="51.5" textAnchor="middle"
                fontSize="4.5" fill="hsl(var(--foreground))" fontWeight="600"
              >
                {data.converged.length}
              </text>
            </>
          )}
        </svg>
        <p className="mt-1 text-center text-xs text-muted-foreground">
          {data.converged.length > 0
            ? `${data.converged.length} roles sit in more than one circle.`
            : "No role sits in more than one circle yet."}
        </p>
      </div>

      {/* The list. Equal weight, always present, keyboard operable. */}
      <ul className="space-y-3">
        {data.circles.map((c, i) => (
          <CircleCard
            key={c.id}
            circle={c}
            index={i}
            selected={selected === c.id}
            onSelect={() => setSelected(selected === c.id ? null : c.id)}
            convergedIds={convergedIds}
          />
        ))}
      </ul>

      {/* The intersection, in words. */}
      <div className="rounded-[24px] border border-border bg-card p-4">
        <h3 className="text-lg font-semibold">Where they converge</h3>
        {data.converged.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No role sits in two of your circles yet. That's a real answer, not a gap — your interests point at
            different kinds of work right now, and you're allowed to follow more than one.
          </p>
        ) : (
          <ol className="mt-3 space-y-4">
            {data.converged.map((r) => (
              <li key={r.ciId}>
                <p className="font-medium">
                  <Link
                    to={`/careers/${r.ciId}`}
                    className="underline decoration-dotted underline-offset-4 hover:decoration-solid"
                  >
                    {r.title}
                  </Link>
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {r.family} · fit {r.fit} · in {r.circleIds.length} of your circles
                  </span>
                  {r.observerCircles > 0 && (
                    <span
                      className="ml-2 rounded-full border border-border px-2 py-0.5 text-xs font-normal text-muted-foreground"
                      title={CONFIDENCE_LABEL[r.confidence]}
                    >
                      {CONFIDENCE_SHORT[r.confidence]}
                    </span>
                  )}
                </p>
                <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                  {r.reasons.map((why) => (
                    <li key={why.circleId}>{why.why}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* What an approved observation did — the receipt, in the learner's words. */}
      {data.observer.present && (
        <div className="rounded-[24px] border border-border bg-card p-4">
          <h3 className="text-lg font-semibold">What {data.observer.observerName ?? data.observer.noun} added</h3>
          <p className="mt-1 text-sm text-muted-foreground">{data.observer.method}</p>

          <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div className="rounded-2xl bg-muted/40 p-3">
              <dt className="text-xs text-muted-foreground">You kept</dt>
              <dd className="font-semibold">{data.observer.itemsApproved}</dd>
            </div>
            <div className="rounded-2xl bg-muted/40 p-3">
              <dt className="text-xs text-muted-foreground">You set aside</dt>
              <dd className="font-semibold">{data.observer.itemsDeclined}</dd>
            </div>
            <div className="rounded-2xl bg-muted/40 p-3">
              <dt className="text-xs text-muted-foreground">Weight per item</dt>
              <dd className="font-semibold">{data.observer.itemWeight}× yours</dd>
            </div>
            <div className="rounded-2xl bg-muted/40 p-3">
              <dt className="text-xs text-muted-foreground">Total counted</dt>
              <dd className="font-semibold">
                {data.observer.appliedTotal} / {data.observer.totalCap}
              </dd>
            </div>
          </dl>

          {data.observer.capApplied && (
            <p className="mt-3 text-sm">
              They said enough to be worth {data.observer.rawTotal}, so the cap held it to{" "}
              {data.observer.appliedTotal}. Nobody else's view of you gets to outweigh your own.
            </p>
          )}

          {data.observer.alreadyYours.length > 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              Counted for nothing, because you'd already told us yourself:{" "}
              {data.observer.alreadyYours.join(", ")}. Agreement isn't extra evidence.
            </p>
          )}

          {data.observer.families.length > 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              Widened your view of{" "}
              {data.observer.families.map((f) => `${f.family} (+${f.strength})`).join(", ")}.
            </p>
          )}

          {data.observer.notes.length > 0 && (
            <ul className="mt-3 space-y-2 text-sm">
              {data.observer.notes.map((n) => (
                <li key={n.id} className="rounded-2xl bg-muted/30 p-3">
                  <span className="block text-xs text-muted-foreground">{n.question}</span>
                  <span className="mt-1 block">{n.answer}</span>
                  {n.edited && (
                    <span className="mt-1 block text-xs text-muted-foreground">
                      You reworded this in your own words before approving it.
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {data.observer.roles.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold">Roles this put in front of you</h4>
              <ul className="mt-2 space-y-1 text-sm">
                {data.observer.roles.map((r) => (
                  <li key={r.ciId} className="flex flex-wrap items-baseline gap-2">
                    <Link
                      to={`/careers/${r.ciId}`}
                      className="underline decoration-dotted underline-offset-4 hover:decoration-solid"
                    >
                      {r.title}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {r.family} · fit {r.fit}
                    </span>
                    <span
                      className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                      title={CONFIDENCE_LABEL[r.confidence]}
                    >
                      {CONFIDENCE_SHORT[r.confidence]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Eligibility audit. */}
      <div className="rounded-[24px] border border-border bg-muted/30 p-4 text-sm">
        <h3 className="font-semibold">What was ruled in before the diagram</h3>
        <p className="mt-1 text-muted-foreground">{data.eligibility.note}</p>
        <p className="mt-2 text-muted-foreground">
          Careers you can reach: {data.eligibility.eligiblePool.toLocaleString()}
          {data.eligibility.ruledOutByStudy > 0 &&
            ` · set aside for now on study reach: ${data.eligibility.ruledOutByStudy.toLocaleString()}`}
          {data.eligibility.studyRelaxed &&
            " · we widened your study limit by one step so your list was never empty"}
        </p>
        {data.eligibility.inputs.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {data.eligibility.inputs.map((i, k) => (
              <li key={`${i.itemCode}-${k}`}>
                <span className="font-mono">{i.itemCode}</span> · {i.question}
                {" → "}
                <span className="text-foreground">{i.answer}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
});
